import type { ComponentProps } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, IconButton, InlineNotification, Text } from '@frontend/design-system';
import { AutosaveStatus, RestoreNotice } from '../../../../draft/draft.types';

interface Props {
  status: AutosaveStatus;
  restoreNotice: RestoreNotice;
  /** Set when the eligibility or draft-existence check failed, so autosave is off until it succeeds. */
  availabilityUnknown?: boolean;
  unresolvedDraft?: boolean;
  isRetryingAvailability?: boolean;
  onRetrySave: () => void;
  onRetryAvailability: () => void;
  onResumeDraft: () => void;
  onDismissRestoreNotice: () => void;
}

/**
 * Recovery guidance above the form (spec §5.2, FR-09). Only one notice is shown, most actionable first;
 * transient states are left to the status indicator.
 */
export const AutosaveRecoveryNotice = (props: Props) =>
  hasRecoveryNotice(props) ? (
    <div css={wrapperStyles}>
      <RecoveryNoticeContent {...props} />
    </div>
  ) : null;

const NOTICE_STATUSES: AutosaveStatus[] = ['paused', 'offline', 'unavailable'];

const hasRecoveryNotice = ({ status, restoreNotice, availabilityUnknown, unresolvedDraft }: Props) =>
  NOTICE_STATUSES.includes(status) || !!availabilityUnknown || !!unresolvedDraft || !!restoreNotice;

const RecoveryNoticeContent = ({
  status,
  restoreNotice,
  availabilityUnknown,
  unresolvedDraft,
  isRetryingAvailability,
  onRetrySave,
  onRetryAvailability,
  onResumeDraft,
  onDismissRestoreNotice,
}: Props) => {
  const { t } = useTranslation('digital-forms-experience');

  const withAction = (
    message: string,
    actionLabel: string,
    onAction: () => void,
    buttonOptions: Pick<ComponentProps<typeof Button>, 'loading' | 'disabled' | 'trackingId'> = {}
  ) => (
    <div css={contentStyles}>
      <Text>{message}</Text>
      <Button variant='secondary' size='small' onClick={onAction} {...buttonOptions}>
        {actionLabel}
      </Button>
    </div>
  );

  if (status === 'paused') {
    return (
      <InlineNotification status='error'>
        {withAction(t("Autosave paused. Recent changes aren't saved."), t('Try again'), onRetrySave)}
      </InlineNotification>
    );
  }

  if (status === 'offline') {
    return (
      <InlineNotification
        status='warn'
        message={t("You're offline. Recent changes aren't saved. They'll save when you reconnect.")}
      />
    );
  }

  if (availabilityUnknown) {
    return (
      <InlineNotification status='warn'>
        {withAction(
          t('Autosave is unavailable. Keep this page open until you submit.'),
          t('Try again'),
          onRetryAvailability,
          { loading: isRetryingAvailability }
        )}
      </InlineNotification>
    );
  }

  if (unresolvedDraft) {
    return (
      <InlineNotification status='warn'>
        {withAction(
          t("Saved progress is available. Restoring it will replace the answers you've entered."),
          t('Restore saved progress'),
          onResumeDraft,
          { disabled: status === 'submitting', trackingId: 'autosave-resume-draft' }
        )}
      </InlineNotification>
    );
  }

  if (status === 'unavailable') {
    return (
      <InlineNotification status='warn' message={t('Autosave is unavailable. Keep this page open until you submit.')} />
    );
  }

  if (restoreNotice === 'partial') {
    return (
      <InlineNotification status='warn'>
        <div css={dismissibleContentStyles}>
          <Text>
            {t(
              'Your saved progress has been restored. Some answers or files could not be restored. Please review and re-enter them.'
            )}
          </Text>
          <IconButton
            label={t('Dismiss')}
            onClick={onDismissRestoreNotice}
            trackingId='autosave-partial-restore-dismiss'
          >
            <Icon name='x' size={16} />
          </IconButton>
        </div>
      </InlineNotification>
    );
  }

  if (restoreNotice === 'restored') {
    return (
      <InlineNotification status='success'>
        <div css={dismissibleContentStyles}>
          <Text>{t('Your saved progress has been restored.')}</Text>
          <IconButton label={t('Dismiss')} onClick={onDismissRestoreNotice} trackingId='autosave-restore-dismiss'>
            <Icon name='x' size={16} />
          </IconButton>
        </div>
      </InlineNotification>
    );
  }

  return null;
};

const wrapperStyles = css`
  margin-bottom: ${theme.spacing(2)};
`;

const dismissibleContentStyles = css`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: ${theme.spacing(1)};
  flex: 1;
  min-width: 0;

  > :first-of-type {
    margin: 0;
    overflow-wrap: anywhere;
  }
`;

const contentStyles = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing(1)};
`;
